import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, LogIn, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LogoMark from "@/components/LogoMark";

const questions = [
  { q: "What would help most right now?", choices: ["Clear my head", "Work through something", "Feel grounded", "I don’t know what I want yet"] },
  { q: "What would you like a little more of?", choices: ["Truth", "Joy", "Hope", "Love"] },
  { q: "How should this space feel?", choices: ["Quiet", "Guided", "Flexible", "Private"] },
];

export default function OnboardingV5() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const done = step >= questions.length;

  const choose = (choice) => {
    const next = [...answers, choice];
    setAnswers(next);
    if (step === 1) {
      try { localStorage.setItem("un_v5_initial_direction", choice.toLowerCase()); } catch {}
    }
    setStep((s) => s + 1);
  };

  const enterPrivate = () => {
    try { localStorage.setItem("un_v5_onboarding_seen", "1"); } catch {}
    nav("/home");
  };

  return (
    <div className="min-h-screen px-5 py-7 flex flex-col max-w-[520px] mx-auto">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/")} className="flex items-center gap-2 text-[10px] uppercase tracking-[.3em]" style={{ color: "var(--un-text-3)" }}><LogoMark size={28} title="" /> UnAnonymous</button>
        <button onClick={() => nav("/auth?mode=signin")} className="text-[13px] font-medium" style={{ color: "var(--un-plum)" }}>Sign in</button>
      </div>

      <div className="flex-1 flex flex-col justify-center py-10">
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: .34 }}>
              <p className="text-[10px] uppercase tracking-[.3em]" style={{ color: "var(--un-text-3)" }}>A little orientation</p>
              <h1 className="mt-2 text-[34px] leading-[1.08] tracking-[-.025em]" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>{questions[step].q}</h1>
              <div className="grid gap-2.5 mt-7">
                {questions[step].choices.map((choice) => (
                  <button key={choice} onClick={() => choose(choice)} className="text-left px-4 py-4 rounded-[1.25rem] un-press" style={{ background: "rgba(255,252,248,.8)", border: "1px solid rgba(91,57,84,.08)", color: "var(--un-text-1)", boxShadow: "0 8px 24px rgba(74,45,72,.035)" }}>
                    <span className="text-[15px]">{choice}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-center gap-1.5 mt-7">{questions.map((_, i) => <span key={i} className="h-1.5 rounded-full transition-all" style={{ width: i === step ? 20 : 6, background: i === step ? "var(--un-ember)" : "rgba(91,57,84,.14)" }} />)}</div>
            </motion.div>
          ) : (
            <motion.div key="done" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <span className="w-14 h-14 rounded-full grid place-items-center" style={{ background: "rgba(196,161,106,.12)", color: "var(--un-plum)" }}><ShieldCheck size={21}/></span>
              <p className="mt-5 text-[10px] uppercase tracking-[.3em]" style={{ color: "var(--un-text-3)" }}>Make this yours</p>
              <h1 className="mt-2 text-[36px] leading-[1.05]" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>Your private space,<br/>with continuity.</h1>
              <p className="mt-4 text-[14px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>Create an account for sign-in continuity across devices, or continue privately with core reflection data staying on this device.</p>
              <div className="grid gap-2.5 mt-7">
                <button onClick={() => nav("/auth?mode=create")} className="py-4 rounded-2xl text-[14px] font-medium flex items-center justify-center gap-2 un-press" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Create my space <ArrowRight size={16}/></button>
                <button onClick={enterPrivate} className="py-4 rounded-2xl text-[14px] flex items-center justify-center gap-2 un-press" style={{ border: "1px solid rgba(91,57,84,.12)", color: "var(--un-plum)" }}>Continue privately</button>
                <button onClick={() => nav("/auth?mode=signin")} className="py-2 text-[13px] flex items-center justify-center gap-2" style={{ color: "var(--un-text-3)" }}><LogIn size={14}/> I already have an account</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
